import React, { Fragment, useEffect, useMemo, useState } from "react";
import TransitionOnLoad from "Components/TransitionOnLoad";
import { Button, Card, CardBody, CardFooter, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import PageHeading from "Layout/AppMain/PageHeading";
import ReactTableGeneric from "Components/ReactTableGeneric";
import Loader from "react-loaders";
import { ReactComponent as AddIcon } from "../../../../assets/utils/images/svgs/addBlue.svg";
import { ReactComponent as CrossIcon } from "../../../../assets/utils/images/svgs/Cross.svg";
import { BsCheckCircle } from "react-icons/bs";
import { toast } from "react-toastify";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import ReplacementCodeService from "Api/Instellingen/ReplacementCode";
import CoordinatorInstellingenService from "Api/Instellingen";
import GroupStatus from "Components/IconDropdown/Cross_Tick/GroupStatus";
import useSWR from "swr";
import { useApiMutation } from "services/useApiMutation";

const validationSchema = Yup.object({
    vCode: Yup.string().required('Code is verplicht'),
    vCodeValue: Yup.string().required('Omschrijving is verplicht').max(50, 'Omschrijving must be at most 50 characters'),
    eKetenregeling: Yup.string().required('Vervanging volgens ketenregeling is verplicht'),
    eExclude_wwz: Yup.string().required('Uitzondering WWZ is verplicht'),
    vTarief: Yup.number()
        .typeError('Must be a number')
        .positive('Must be a positive number')
        .test('is-decimal', 'Moet maximaal 2 decimalen bevatten', value =>
            value === undefined || value === null
                ? true
                : /^\d+(\.\d{1,2})?$/.test(value.toString())
        )
});

const ReplacementCodes = () => {
    const [communityId, setCommunityId] = useState('');
    const [modal, setModal] = useState({
        state: false,
        type: "add",
        rowData: {}
    });

    const { trigger: saveCodeMutation } = useApiMutation();

    const {
        data: communityResponse,
        isLoading: communityLoading,
    } = useSWR(
        "community-list",
        CoordinatorInstellingenService.communityList
    );

    const communityList =
        communityResponse?.data?.communityList || [];

    useEffect(() => {
        if (!communityId && communityList.length) {
            setCommunityId(
                communityList[0].iCommunityId
            );
        }
    }, [communityList, communityId]);

    const {
        data: codesResponse,
        mutate: refreshCodes,
        isLoading: codesLoading,
    } = useSWR(
        communityId
            ? ["replacement-codes", communityId]
            : null,
        ([, communityId]) =>
            ReplacementCodeService.fetchCodesData(
                communityId
            )
    );

    const codesData =
        codesResponse?.data?.data?.vervangingsList || [];

    const totalCount = codesResponse?.data?.data?.totalCount;

    const costBearerData =
        codesResponse?.data?.data?.kostendragers || [];

    const columns = React.useMemo(
        () => [
            {
                header: () => 'Code',
                accessorKey: 'vCode',
                cell: (value) => {
                    return <div className="cursor-pointer" onClick={() => {
                        setModal({
                            state: true,
                            type: 'edit',
                            rowData: value.row.original
                        })
                    }}>{value.row.original.vCode}</div>
                }
            },
            {
                header: () => 'Omschrijving',
                accessorKey: 'vCodeValue',
                size: 566,   
                minSize: 350,
                cell: (value) => {
                    return <div>{value.row.original.vCodeValue}</div>
                }
            },
            {
                header: () => 'Categorie',
                accessorKey: 'eCategory',
            },
            {
                header: () => 'Vervanging',
                accessorKey: 'eKetenregeling',
                cell: (value) => {
                    return <div className="d-flex justify-content-center">
                        {
                            value.row.original.eKetenregeling === "Yes" ?
                                <BsCheckCircle color='#39A825' fontSize={"18px"} /> :
                                <CrossIcon color='#FB2E5F' fontSize={"24px"} />
                        }
                    </div>
                },
            },
            {
                header: () => 'Kostendrager',
                accessorKey: 'eKostendrager',
            },
            {
                header: () => 'Status',
                accessorKey: 'eStatus',
                cell: (value) => {
                    return <div className="d-flex justify-content-center">
                        {
                            value.row.original.eStatus === "Active" ?
                                <BsCheckCircle color='#39A825' fontSize={"18px"} /> :
                                <CrossIcon color='#FB2E5F' fontSize={"24px"} />
                        }
                    </div>
                },
            }],
        [communityId]
    );

    const toggle = () => {
        setModal(prev => ({
            ...prev,
            state: !prev.state,
        }))
    };

    const formInitialValues = useMemo(() => {
        const normalizeDecimalValue = (value) => {
            if (typeof value === 'string' && value) {
                return parseFloat(value.replace(',', '.')); // Replace ',' with '.' and parse as a number
            }
            return value;
        };
        if (modal.type === "edit") return {
            vCode: modal.rowData.vCode,
            vCodeValue: modal.rowData.vCodeValue,
            eCategory: modal.rowData.eCategory || "Verlof",
            eStatus: modal.rowData.eStatus || "Active",
            eKetenregeling: modal.rowData.eKetenregeling,
            eExclude_wwz: modal.rowData.eExclude_wwz,
            eKostendrager: modal.rowData.eKostendrager,
            vTarief: normalizeDecimalValue(modal.rowData.vTarief),
        }
        else return {
            vCode: '',
            vCodeValue: '',
            eCategory: 'Verlof',
            eStatus: 'Active',
            eKetenregeling: 'No',
            eExclude_wwz: 'No',
            eKostendrager: '',
            vTarief: '',
        }
    }, [modal.type, modal.rowData]);

    const handleOrgSubmit = (values) => {
        const data = {
            iCommunityId: communityId,
            vCode: values.vCode,
            vCodeValue: values.vCodeValue,
            eCategory: values.eCategory,
            eStatus: values.eStatus,
            eKetenregeling: values.eKetenregeling,
            eExclude_wwz: values.eExclude_wwz,
            eKostendrager: values.eKostendrager,
            vTarief: values.vTarief,
            type: modal.type
        };
        if (modal.type === "edit") {
            data.iCodeId = modal.rowData.iCodeId
        }
        saveCodeMutation(() =>
            ReplacementCodeService.saveCodeApi(data)).then((response) => {
                if (response.status) {
                    refreshCodes();
                    setModal(false);
                } else {
                    toast.error("Er is iets misgegaan");
                }
            }).catch(() => console.error);
    };

    const orgName = useMemo(() => {
        return communityList?.find(item => item.iCommunityId === Number(communityId))?.name || ""
    }, [communityId, communityList]);

    return (
        <Fragment>
            <PageHeading orgName={orgName} />
            <TransitionOnLoad>
                <Row className="mb-5">
                    <Col>
                        <Card className="main-card mb-3">
                            <CardBody className="p-3">
                                {communityLoading || codesLoading ?
                                    <Loader type="ball-grid-pulse" className={'mt-3 d-flex mx-auto justify-content-center'} /> :
                                    <ReactTableGeneric
                                        data={codesData || []}
                                        columns={columns}
                                        tableClassName="mb-0 table table-borderless table-sm table-hover tb-codes"
                                        rowCount={20}
                                        filters={{ search: false, pagination: true, sorting: false }}
                                        count={totalCount}
                                    />
                                }
                            </CardBody>
                        </Card>
                    </Col>
                    <Modal isOpen={modal.state} toggle={toggle} className="modal-group-state feedback-modal-style link-form">
                        <ModalHeader toggle={toggle}>
                            Vervangingscode toevoegen/wijzigen
                        </ModalHeader>
                        <ModalBody>
                            <Formik
                                initialValues={formInitialValues}
                                validationSchema={validationSchema}
                                onSubmit={handleOrgSubmit}
                                enableReinitialize={true}
                            >
                                {({ setFieldValue, handleSubmit, values, handleChange, handleBlur, isSubmitting }) => (
                                    <Form onSubmit={handleSubmit}>
                                        <FormGroup>
                                            <Label for="vCode">Code *</Label>
                                            <Input name="vCode"
                                                type="text" onChange={handleChange} onBlur={handleBlur}
                                                value={values.vCode} />
                                            <ErrorMessage name="vCode" component="small"
                                                className="block mt-2 invalid-feedback" />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label for="vCodeValue">Omschrijving *</Label>
                                            <Input name="vCodeValue"
                                                type="text" onChange={handleChange} onBlur={handleBlur}
                                                value={values.vCodeValue} />
                                            <ErrorMessage name="vCodeValue" component="small"
                                                className="block mt-2 invalid-feedback" />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label for="eCategory">Selecteer categorie</Label>
                                            <Input
                                                id="eCategory"
                                                name="eCategory"
                                                type="select"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.eCategory}
                                            >
                                                <option value="Verlof">Verlof</option>
                                                <option value="Verzuim">Verzuim</option>
                                                <option value="Overig">Overig</option>
                                            </Input>
                                        </FormGroup>
                                        <FormGroup className="d-flex justify-content-between mt-4">
                                            <Label for="eStatus">Selecteer status</Label>
                                            <GroupStatus iconChangeHandler={(_, value) => { setFieldValue('eStatus', value === '0' ? 'Inactive' : 'Active') }} field={"eStatus"} data={values.eStatus === 'Active' ? 1 : 0} />
                                        </FormGroup>
                                        <FormGroup className="d-flex justify-content-between mt-4">
                                            <Label for="eKetenregeling">Vervanging volgens ketenregeling *</Label>
                                            <GroupStatus iconChangeHandler={(_, value) => { setFieldValue('eKetenregeling', value === '0' ? 'No' : 'Yes') }} field={"eKetenregeling"} data={values.eKetenregeling === 'Yes' ? 1 : 0} />
                                        </FormGroup>
                                        <FormGroup className="d-flex justify-content-between mt-4">
                                            <Label for="eExclude_wwz">Uitzondering WWZ *</Label>
                                            <GroupStatus iconChangeHandler={(_, value) => { setFieldValue('eExclude_wwz', value === '0' ? 'No' : 'Yes') }} field={"eExclude_wwz"} data={values.eExclude_wwz === 'Yes' ? 1 : 0} />

                                        </FormGroup>
                                        <FormGroup>
                                            <Label for="eKostendrager">Selecteer kostendrager</Label>
                                            <Input
                                                id="eKostendrager"
                                                name="eKostendrager"
                                                type="select"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.eKostendrager}
                                            >
                                                <option value="">--- Geen kostendrager ---</option>
                                                {costBearerData?.map(item => {
                                                    return <option key={item.id}>{item.vKostendrager}</option>
                                                })}
                                            </Input>
                                        </FormGroup>
                                        <FormGroup>
                                            <Label for="vTarief">Tarief (€)</Label>
                                            <Input name="vTarief"
                                                type="text" onChange={handleChange} onBlur={handleBlur}
                                                value={values.vTarief} />
                                            <ErrorMessage name="vTarief" component="small"
                                                className="block mt-2 invalid-feedback" />
                                        </FormGroup>
                                        <div className="ms-1 mt-2" style={{ color: "#878EA6" }}>
                                            <span className="text-danger">*</span> Verplicht veld
                                        </div>
                                        <ModalFooter className="d-flex align-items-center justify-content-center border-bottom-0 py-4 mt-4">
                                            <Button className="btn-color-style opslaan-btn" size="sm" outline color="primary" type="submit" disabled={isSubmitting}>
                                                Toepassen
                                            </Button>
                                        </ModalFooter>
                                    </Form>
                                )}
                            </Formik>
                        </ModalBody>
                    </Modal>
                </Row>
            </TransitionOnLoad>
            <div className="chat-layout-footer verzoken-footer-height">
                <CardFooter className="chat-layout-footer__inner">
                    <div className="d-flex gap-3 mr-4">
                        <div>
                            <select className={`form-select`} value={communityId} onChange={(e) => { setCommunityId(e.target.value) }}>
                                <option value={''} disabled>Alle besturen</option>
                                {communityList?.map(item => {
                                    return <option key={item.iCommunityId} value={item.iCommunityId}>{item.name}</option>
                                })}
                            </select>
                        </div>
                        <Button
                            outline
                            className="btn-outline-primary-blue btn-color-style py-2 px-3 d-flex gap-2 align-items-center"
                            onClick={() => {
                                setModal({
                                    state: true,
                                    type: "add",
                                    rowData: {}
                                });
                            }}
                            type="button"
                            color="primary"
                        >
                            Vervangingscodes <AddIcon />
                        </Button>
                    </div>
                </CardFooter>
            </div>
        </Fragment>
    );
};

export default ReplacementCodes;
